import { useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useParams, useSearchParams } from "react-router-dom";
import { api, type AuditLog, type Page, type SystemEvent } from "@/lib/api";
import {
  Button,
  Empty,
  ErrorNote,
  Loading,
  Mono,
  Panel,
  Select,
  Status,
} from "@/components/primitives";

/**
 * System and audit logs (spec §12).
 *
 * Two different questions, so two pages: the system log answers "what did the
 * bot do", the audit log answers "who changed what". The dashboard's alerts
 * are counted straight off the same system_events rows, so the two can never
 * disagree.
 */

function Pager({
  page,
  total,
  pageSize,
  onPage,
}: {
  page: number;
  total: number;
  pageSize: number;
  onPage: (page: number) => void;
}) {
  const pages = Math.ceil(total / pageSize);
  if (pages <= 1) return null;

  return (
    <div className="flex items-center justify-end gap-2 border-t border-border px-5 py-2">
      <Button variant="quiet" size="sm" disabled={page === 1} onClick={() => onPage(page - 1)}>
        Previous
      </Button>
      <span className="text-muted-foreground">
        Page {page} of {pages}
      </span>
      <Button variant="quiet" size="sm" disabled={page >= pages} onClick={() => onPage(page + 1)}>
        Next
      </Button>
    </div>
  );
}

export function SystemLogPage() {
  const { botId } = useParams();
  const [page, setPage] = useState(1);
  const [severity, setSeverity] = useState("");

  // A dashboard alert links here with ?type=..., so the number it shows leads
  // to the events behind it rather than to every error (decision 49).
  const [search, setSearch] = useSearchParams();
  const type = search.get("type");

  const params = new URLSearchParams({ page: String(page) });
  if (severity) params.set("severity", severity);
  if (type) params.set("type", type);

  const query = useQuery({
    queryKey: ["system-events", botId, params.toString()],
    queryFn: () => api.get<Page<SystemEvent>>(`/bots/${botId}/system-events?${params}`),
    placeholderData: keepPreviousData,
  });

  return (
    <div className="grid grid-cols-1 gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <Select
          value={severity}
          onChange={(e) => {
            setSeverity(e.target.value);
            setPage(1);
          }}
        >
          <option value="">Everything</option>
          <option value="error">Errors only</option>
          <option value="warn">Warnings</option>
          <option value="info">Info</option>
          <option value="debug">Debug</option>
        </Select>
        {type && (
          <Button
            size="sm"
            variant="quiet"
            onClick={() => {
              setSearch({});
              setPage(1);
            }}
          >
            Showing {type} — clear
          </Button>
        )}
        {query.data && (
          <span className="ml-auto text-muted-foreground">
            {query.data.total.toLocaleString()} entries
          </span>
        )}
      </div>

      {query.error && <ErrorNote error={query.error} />}

      <Panel>
        {query.isLoading && !query.data ? (
          <Loading />
        ) : query.data && query.data.rows.length === 0 ? (
          <Empty
            title="Nothing logged"
            hint="Anything the bot decided not to do, or could not do, is recorded here."
          />
        ) : (
          <>
            <ul className="divide-y divide-border/60">
              {query.data?.rows.map((event) => (
                <li key={event.id} className="grid grid-cols-1 gap-1 px-5 py-2">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
                    <Status value={event.severity} />
                    <Mono className="min-w-0 [overflow-wrap:anywhere]">{event.type}</Mono>
                    <span className="ml-auto text-muted-foreground">
                      {new Date(event.createdAt).toLocaleString()}
                    </span>
                  </div>
                  {event.detail !== null && event.detail !== undefined && (
                    <Mono className="whitespace-pre-wrap text-muted-foreground [overflow-wrap:anywhere]">
                      {JSON.stringify(event.detail)}
                    </Mono>
                  )}
                </li>
              ))}
            </ul>
            {query.data && (
              <Pager
                page={query.data.page}
                total={query.data.total}
                pageSize={query.data.pageSize}
                onPage={setPage}
              />
            )}
          </>
        )}
      </Panel>
    </div>
  );
}

export function AuditLogPage() {
  const { botId } = useParams();
  const [page, setPage] = useState(1);

  const query = useQuery({
    queryKey: ["audit-logs", botId, page],
    queryFn: () => api.get<Page<AuditLog>>(`/bots/${botId}/audit-logs?page=${page}`),
    placeholderData: keepPreviousData,
  });

  if (query.isLoading && !query.data) return <Loading />;
  if (query.error) return <ErrorNote error={query.error} />;

  return (
    <Panel>
      {query.data && query.data.rows.length === 0 ? (
        <Empty
          title="No changes recorded yet"
          hint="Every admin action and every group command lands here, with what changed."
        />
      ) : (
        <>
          <ul className="divide-y divide-border/60">
            {query.data?.rows.map((entry) => (
              <li key={entry.id} className="grid grid-cols-1 gap-1 px-5 py-2">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
                  <Mono className="font-medium">{entry.action}</Mono>
                  <span className="min-w-0 text-muted-foreground [overflow-wrap:anywhere]">
                    {/* Group commands carry a Telegram id, panel actions an
                        Access email (spec §18.4). */}
                    {entry.actor.startsWith("telegram:")
                      ? `Telegram ${entry.actor.slice("telegram:".length)}`
                      : entry.actor}
                  </span>
                  {entry.actorRole && (
                    <span className="text-muted-foreground">{entry.actorRole}</span>
                  )}
                  <span className="ml-auto text-muted-foreground">
                    {new Date(entry.createdAt).toLocaleString()}
                  </span>
                </div>
                {Boolean(entry.beforeJson ?? entry.afterJson) && (
                  <Mono className="whitespace-pre-wrap text-muted-foreground [overflow-wrap:anywhere]">
                    {`${entry.beforeJson ? `${JSON.stringify(entry.beforeJson)} → ` : ""}${JSON.stringify(entry.afterJson)}`}
                  </Mono>
                )}
              </li>
            ))}
          </ul>
          {query.data && (
            <Pager
              page={query.data.page}
              total={query.data.total}
              pageSize={query.data.pageSize}
              onPage={setPage}
            />
          )}
        </>
      )}
    </Panel>
  );
}
